
import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Grid,
  Divider,
  CircularProgress,
  Paper,
  TextField,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
// import Cookies from "js-cookie";

const AODASupervisionNotePreview = ({ data }) => {
  const [note, setNote] = useState({});
  const [formMeta, setFormMeta] = useState({});
  const [sigImages, setSigImages] = useState({}); // { client }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const API_BASE_URL =
    process.env.NEXT_PUBLIC_API_BASE_URL || process.env.REACT_APP_API_URL;

  const { patientFormId, formTitle, practiceLogo, particeName } = data || {};

  // ========== American date helpers ==========
  const toDate = (val) => {
    if (!val) return null;
    if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
    if (typeof val === "number") {
      const d = new Date(val);
      return isNaN(d.getTime()) ? null : d;
    }
    const s = String(val).trim();

    // ISO: 2025-10-01 or 2025-10-01T06:21:32.433
    let m = s.match(
      /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?)?/
    );
    if (m) {
      const yyyy = +m[1],
        mm = +m[2] - 1,
        dd = +m[3];
      const hh = +(m[4] || 0),
        min = +(m[5] || 0),
        sec = +(m[6] || 0);
      return new Date(yyyy, mm, dd, hh, min, sec);
    }

    // US: MM/DD/YYYY [hh:mm AM/PM]
    m = s.match(
      /^(\d{1,2})[\/-](\d{1,2})[\/-](\d{2,4})(?:\s+(\d{1,2}):(\d{2})\s*(AM|PM))?$/i
    );
    if (m) {
      let mm = +m[1],
        dd = +m[2],
        yyyy = +m[3];
      if (yyyy < 100) yyyy += 2000;
      let hh = +(m[4] || 0),
        min = +(m[5] || 0);
      const ap = (m[6] || "").toUpperCase();
      if (ap === "PM" && hh < 12) hh += 12;
      if (ap === "AM" && hh === 12) hh = 0;
      return new Date(yyyy, mm - 1, dd, hh, min, 0);
    }

    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  };

  const fmtDate = useCallback((val) => {
    const d = toDate(val);
    if (!d) return "—";
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }, []);

  const fmtDateTime = useCallback((val) => {
    const d = toDate(val);
    if (!d) return "";
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = d.getFullYear();
    let h = d.getHours();
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    const hh = String(h).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${mm}/${dd}/${yyyy} ${hh}:${min} ${ampm}`;
  }, []);

  // ========== Utils ==========
  const firstNonEmpty = (...vals) =>
    vals.find((v) => v !== undefined && v !== null && String(v).trim() !== "") || "";

  const parseBool = (v) => {
    if (typeof v === "boolean") return v;
    if (v === 1) return true;
    if (v === 0) return false;
    const s = String(v).trim().toLowerCase();
    return ["true", "yes", "y", "1"].includes(s);
  };

  // Safe JSON
  async function fetchJSONSafe(url, options = {}) {
    const res = await fetch(url, {
      headers: { Accept: "application/json", ...(options.headers || {}) },
      ...options,
    });
    let data = {};
    if (res.status !== 204) {
      const text = await res.text();
      if (text && text.trim()) {
        try {
          data = JSON.parse(text);
        } catch {
          data = {};
        }
      }
    }
    return { ok: res.ok, status: res.status, data };
  }

  async function getClientImage(path, token) {
    try {
      if (!path) return "";
      if (/^(https?:|data:)/i.test(path)) return path;
      const url = `${API_BASE_URL}/PatientDocument/ViewPatientDocument?path=${encodeURIComponent(
        path
      )}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return "";
      const ct = (res.headers.get("content-type") || "").toLowerCase();
      if (ct.includes("text/plain")) {
        const link = await res.text();
        return link || "";
      }
      const blob = await res.blob();
      return blob && blob.size ? URL.createObjectURL(blob) : "";
    } catch {
      return "";
    }
  }

  // ===== Signature phrase builder (strict: requires date) =====
  const fromEmail = (email) => {
    if (!email) return { name: "—", username: "" };
    const local = String(email).split("@")[0] || "";
    const formatted = local
      .replace(/[._-]/g, " ")
      .replace(/\b\w/g, (m) => m.toUpperCase());
    return { name: formatted || "—", username: local || "" };
  };

  const mkPhraseFromEmailStrict = useCallback(
    (email, dt) => {
      const when = fmtDateTime(dt);
      if (!when) return ""; // No date => treat as no signature
      const { name, username } = fromEmail(email);
      const who = username ? `${name}, ${username}` : name || "—";
      return `Digitally signed by ${who} on ${when}.`;
    },
    [fmtDateTime]
  );

  // ========== Data fetching ==========
  useEffect(() => {
    let active = true;

    (async () => {
      setNote({});
      setFormMeta({});
      setSigImages({});
      setLoading(true);
      setError("");

      try {
        const token = localStorage.getItem('neuro_patientportal_auth_token')
        if (!token) throw new Error("Authentication token not found.");
        if (!API_BASE_URL) throw new Error("API base URL is not configured.");
        const headers = { Authorization: `Bearer ${token}` };

        const [formRes, noteRes] = await Promise.all([
          fetchJSONSafe(
            `${API_BASE_URL}/Forms/GetPatientFormsbyId?Id=${patientFormId}`,
            { headers }
          ),
          fetchJSONSafe(
            `${API_BASE_URL}/Supervision/GetSupervisionNoteById?patientFormId=${patientFormId}`,
            { headers }
          ),
        ]);

        if (!active) return;

        const formRaw = formRes.ok ? formRes.data : {};
        const noteRaw = noteRes.ok ? noteRes.data : {};

        const formData = Array.isArray(formRaw) ? formRaw[0] || {} : formRaw || {};
        const noteData = Array.isArray(noteRaw) ? noteRaw[0] || {} : noteRaw || {};

        setFormMeta(formData);
        setNote(noteData);

        // client signature image
        const clientPath = firstNonEmpty(
          noteData.clientsURL,
          noteData.ClientsURL,
          noteData.clientSignatureURL,
          noteData.patientSignatureURL,
          formData.clientsURL,
          formData.ClientsURL,
          formData.clientSignatureURL,
          formData.patientSignatureURL
        );
        const img = await getClientImage(clientPath, token);
        if (!active) return;
        setSigImages({ client: img });
      } catch (e) {
        if (active) setError(e.message || "Failed to load data");
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [patientFormId, API_BASE_URL]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={5}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" align="center" sx={{ p: 3 }}>
        {error}
      </Typography>
    );
  }

  // ========== Derived (meta/header) ==========
  const sessionDate = fmtDate(
    firstNonEmpty(
      note.assessmentDate,
      formMeta.assessmentDate,
      note.sessionDate,
      formMeta.sessionDate,
      formMeta.SessionDate,
      formMeta.dateOfService,
      formMeta.DOS
    )
  );

  const titleText =
    firstNonEmpty(
      note.customFormTitle,
      formMeta.customFormTitle,
      note.title,
      formMeta.title,
      formMeta.formTitle,
      formMeta.Title,
      formTitle
    ) || "AODA Supervision/Collaboration Note";

  const clientName =
    firstNonEmpty(
      note.patientName,
      formMeta.patientName,
      note.clientName,
      formMeta.clientName,
      formMeta.ClientName
    ) || "—";

  const dobText = fmtDate(
    firstNonEmpty(note.dob, formMeta.dob, formMeta.DOB, formMeta.dateOfBirth)
  );

  const fileNoVal =
    firstNonEmpty(
      formMeta.fileNo,
      formMeta.FileNo,
      formMeta.fileNumber,
      formMeta.fileno,
      formMeta.accountNo
    ) || "—";

  // Flags
  const printed = parseBool(firstNonEmpty(note.isPrinted, formMeta.isPrinted));
  const auditReady = parseBool(
    firstNonEmpty(note.isAuditReady, formMeta.isAuditReady)
  );

  // Inactive should mirror main form's usage of "isChecked"
  const inactive = parseBool(
    firstNonEmpty(
      note.isChecked,        // backend uses this for Inactive in main form
      formMeta.isChecked,
      note.inactive,         // fallbacks
      formMeta.inactive,
      formMeta.Isinactive
    )
  );

  // Lock should NOT use isChecked; use explicit lock fields if any
  const locked = parseBool(
    firstNonEmpty(
      note.isLocked,
      formMeta.isLocked,
      note.formlock,
      formMeta.formlock,
      note.islocked,
      formMeta.islocked
    )
  );

  // Form checkboxes
  const clinicalSupervision = parseBool(
    firstNonEmpty(
      note.clinicalSupervision,
      formMeta.clinicalSupervision,
      formMeta.ClinicalSupervision
    )
  );
  const clinicalCollaboration = parseBool(
    firstNonEmpty(
      note.clinicalCollaboration,
      formMeta.clinicalCollaboration,
      formMeta.ClinicalCollaboration
    )
  );
  const isIndividual = parseBool(
    firstNonEmpty(
      note.individual,
      note.isIndividual,
      formMeta.individual,
      formMeta.isIndividual,
      formMeta.Individual
    )
  );
  const isGroup = parseBool(
    firstNonEmpty(
      note.groups,
      note.isGroup,
      formMeta.groups,
      formMeta.isGroup,
      formMeta.Group,
      formMeta.groupSelected,
      formMeta.isGroupSelected
    )
  );

  // Staff + Discussion
  const staffText =
    firstNonEmpty(
      note.staff,
      formMeta.staff,
      note.staffName,
      formMeta.staffName,
      note.staffMember,
      formMeta.staffMember,
      note.clinicianName,
      formMeta.clinicianName
    ) || "—";

  const discussionText =
    firstNonEmpty(
      note.disscussion, // typo in sample
      note.discussion,
      note.discussionText,
      note.discussions,
      note.discussionObservationsRecommendations,
      note.DiscussionObservationsRecommendations,
      note.description,
      note.notes,
      note.remarks,
      formMeta.discussion,
      formMeta.description,
      formMeta.notes
    ) || "—";

  // ========== Signatures (STRICT: show only when date exists) ==========
  const internPhrase = mkPhraseFromEmailStrict(
    firstNonEmpty(
      note.internSignURL,
      formMeta.internSignURL,
      note.internSignatureURL,
      formMeta.internSignatureURL
    ),
    firstNonEmpty(
      note.staffsignatureDATE,
      formMeta.staffsignatureDATE,
      note.internSignatureDATE,
      formMeta.internSignatureDATE,
      note.internSignatureDate,
      formMeta.internSignatureDate,
      note.internSignatureDateTime
    )
  );

  const supervisedPhrase = mkPhraseFromEmailStrict(
    firstNonEmpty(
      note.supervisedClinicianURL,
      formMeta.supervisedClinicianURL,
      note.cliniciansignatureURL,
      note.clinicianSignatureURL,
      formMeta.clinicianSignatureURL
    ),
    firstNonEmpty(
      note.supervisedDate,
      formMeta.supervisedDate,
      note.cliniciansignatureDATE,
      note.clinicianSignatureDATE,
      formMeta.clinicianSignatureDATE
    )
  );

  const supervisorPhrase = mkPhraseFromEmailStrict(
    firstNonEmpty(
      note.supervisorSignURL,
      formMeta.supervisorSignURL,
      note.supervisorSignatureURL,
      formMeta.supervisorSignatureURL
    ),
    firstNonEmpty(
      note.supervisorSignatureDATE,
      formMeta.supervisorSignatureDATE
    )
  );

  const directorPhrase = mkPhraseFromEmailStrict(
    firstNonEmpty(
      note.treatmentdirectorURL,
      formMeta.treatmentdirectorURL,
      note.directorSignatureURL,
      formMeta.directorSignatureURL
    ),
    firstNonEmpty(
      note.directorDate,
      formMeta.directorDate,
      note.treatmentDirectorSignatureDATE,
      formMeta.treatmentDirectorSignatureDATE
    )
  );

  // Client image + signed-on date
  const clientSignedOn = fmtDate(
    firstNonEmpty(
      note.clientSignatureDATE,
      formMeta.clientSignatureDATE,
      note.clientSignatureDate,
      formMeta.clientSignatureDate
    )
  );
  const clientTypedName = firstNonEmpty(
    note.clientSignatureName,
    note.patientSignatureName,
    clientName
  );

  // Client row: show — if no image AND no date
  const renderImageSignature = (label, imgLink, typedName, dateText) => {
    const hasDate = !!(dateText && dateText !== "—");
    const hasSignature = !!imgLink || hasDate;

    return (
      <tr>
        <td style={{ padding: 8, width: "25%", verticalAlign: "top" }}>
          <Typography>{label}</Typography>
        </td>
        <td style={{ padding: 8, width: "75%", verticalAlign: "top" }}>
          {hasSignature ? (
            imgLink ? (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <img
                  src={imgLink}
                  alt={`${label} Image`}
                  style={{
                    display: "block",
                    width: 200,
                    height: 80,
                    border: "2px solid #000",
                    backgroundColor: "#f5f5f5",
                    padding: 10,
                    borderRadius: 8,
                    objectFit: "contain",
                  }}
                />
                {hasDate ? (
                  <p style={{ fontWeight: 700, fontSize: 16, margin: 0 }}>
                    Signed on {dateText}
                  </p>
                ) : null}
              </div>
            ) : (
              <div>
                {typedName ? (
                  <Typography sx={{ fontWeight: 600 }}>{typedName}</Typography>
                ) : null}
                {hasDate ? (
                  <Typography variant="body2" color="text.secondary">
                    Signed on {dateText}
                  </Typography>
                ) : null}
              </div>
            )
          ) : (
            <Typography color="text.secondary">—</Typography>
          )}
        </td>
      </tr>
    );
  };

  const renderDigitalRow = (label, phraseText) => (
    <tr>
      <td style={{ padding: 8, width: "25%", verticalAlign: "top" }}>
        <Typography>{label}</Typography>
      </td>
      <td style={{ padding: 8, width: "75%", verticalAlign: "top" }}>
        {phraseText ? (
          <Typography sx={{ fontWeight: 600 }}>{phraseText}</Typography>
        ) : (
          <Typography color="text.secondary">—</Typography>
        )}
      </td>
    </tr>
  );

  // ========== UI ==========
  return (
    <Paper
      sx={{
        p: 0,
        borderRadius: 2,
        boxShadow: "0 3px 8px rgba(0,0,0,0.08)",
        bgcolor: "#fff",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          position: "sticky",
          top: "-16px",
          zIndex: (t) => t.zIndex.appBar,
          bgcolor: "#fff",
          p: 4,
          pb: 0,
          mb: 3,
        }}
      >
        <Typography variant="subtitle2" sx={{ position: "absolute", top: 16, left: 16 }}>
          <strong>DOS:</strong> {sessionDate}
        </Typography>
        <Typography
          variant="h6"
          sx={{
            position: "absolute",
            top: 16,
            left: "50%",
            transform: "translateX(-50%)",
            fontWeight: "bold",
          }}
        >
          {titleText}
        </Typography>

        {practiceLogo ? (
          <Box
            component="img"
            src={practiceLogo}
            alt="Practice Logo"
            sx={{ width: 65, height: 65, borderRadius: 1, mb: 2, mx: "auto" }}
          />
        ) : null}
        <Typography variant="h6" sx={{ textAlign: "center", fontWeight: "bold" }}>
          {particeName || ""}
        </Typography>

        <Box sx={{ height: 28 }} />
        <Divider />
      </Box>

      {/* Patient strip (File # | Client | DOB) */}
      <Box sx={{ px: 4 }}>
        <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>File #</strong> {fileNoVal}
            </Typography>
          </Grid>
          <Grid item xs={4} sx={{ textAlign: "center" }}>
            <Typography variant="body2">
              <strong>Client:</strong> {clientName}
            </Typography>
          </Grid>
          <Grid item xs={4} sx={{ textAlign: "right" }}>
            <Typography variant="body2">
              <strong>DOB:</strong> {dobText}
            </Typography>
          </Grid>
        </Grid>
        <Divider sx={{ mb: 3 }} />
      </Box>

      {/* Title + Session Date */}
      <Box sx={{ px: 4, mb: 2 }}>
        <Grid container spacing={0} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              size="small"
              label="Title"
              value={titleText}
              InputProps={{ readOnly: true, disabled: true }}
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiInputBase-root": {
                  height: 40,
                  fontSize: "0.900rem",
                  padding: "0 10px",
                  width: "350px",
                },
              }}
            />
          </Grid>

          <Divider sx={{ my: 2, mx: 5 }} />

          <Grid item xs={12} md={6}>
            <TextField
              size="small"
              label="Session Date"
              value={sessionDate}
              InputProps={{ readOnly: true, disabled: true }}
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiInputBase-root": {
                  height: 40,
                  fontSize: "0.900rem",
                  padding: "0 10px",
                  width: "350px",
                },
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Flags */}
      <Box sx={{ px: 4, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item>
            <FormControlLabel
              control={<Checkbox checked={printed} disabled />}
              label="Printed and Filed"
            />
          </Grid>
          <Grid item>
            <FormControlLabel
              control={<Checkbox checked={inactive} disabled />}
              label="Inactive"
            />
          </Grid>
          <Grid item>
            <FormControlLabel
              control={<Checkbox checked={auditReady} disabled />}
              label="Audit Ready"
            />
          </Grid>
          <Grid item>
            <FormControlLabel
              control={<Checkbox checked={locked} disabled />}
              label="Lock"
            />
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 3, mx: 4 }} />

      {/* Supervision / Case Consultation */}
      <Box sx={{ px: 4, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
          Supervision / Case Consultation
        </Typography>

        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="body2" sx={{ minWidth: 100, fontWeight: 500 }}>
            Duration:
          </Typography>
          <TextField
            size="small"
            value={firstNonEmpty(note.duration, formMeta.duration, "—")}
            InputProps={{ readOnly: true, disabled: true }}
            sx={{ width: 240 }}
          />
        </Box>

        <Grid container spacing={2}>
          <Grid item>
            <FormControlLabel
              control={<Checkbox checked={clinicalSupervision} disabled />}
              label="Clinical Supervision"
            />
          </Grid>
          <Grid item>
            <FormControlLabel
              control={<Checkbox checked={clinicalCollaboration} disabled />}
              label="Clinical Collaboration"
            />
          </Grid>
          <Grid item>
            <FormControlLabel
              control={<Checkbox checked={isIndividual} disabled />}
              label="Individual"
            />
          </Grid>
          <Grid item>
            <FormControlLabel
              control={<Checkbox checked={isGroup} disabled />}
              label="Group"
            />
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 2, mx: 4 }} />

      {/* Staff */}
      <Box sx={{ px: 4, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
          Staff
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={3}
          value={staffText}
          InputProps={{ readOnly: true, disabled: true }}
        />
      </Box>

      <Divider sx={{ my: 2, mx: 4 }} />

      {/* Discussion */}
      <Box sx={{ px: 4, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
          Discussion, Observations, and Recommendations
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={3}
          value={discussionText}
          InputProps={{ readOnly: true, disabled: true }}
        />
      </Box>

      <Divider sx={{ my: 2, mx: 4 }} />

      {/* Signatures */}
      <Box sx={{ px: 4, mt: 3, pb: 4 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
          Signatures
        </Typography>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody>
            {renderImageSignature(
              "Client:",
              sigImages.client || note.clientSignatureURL || "",
              clientTypedName,
              clientSignedOn
            )}
            {renderDigitalRow("Intern Signature:", internPhrase)}
            {renderDigitalRow("Supervised Clinician:", supervisedPhrase)}
            {renderDigitalRow("Supervisor Signature:", supervisorPhrase)}
            {renderDigitalRow("ARMHS Treatment Director:", directorPhrase)}
          </tbody>
        </table>
      </Box>
    </Paper>
  );
};

export default AODASupervisionNotePreview;
